import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../../constants';
import SEO from '../../shared/SEO';
import LoadingScreen from '../../shared/LoadingScreen';
import FadeImage from '../../shared/FadeImage';
import { createPlaceholderUrl } from '../../../utils/PlaceholderCreator';
import { Ruler, RotateCw, RotateCcw, MoveRight, Calendar, MapPin, Trophy, Timer } from 'lucide-react';
import { ROUTES } from '../../../routes';
import CourseMap from './CourseMap/CourseMap';

const RaceDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [race, setRace] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchRace = async () => {
            try {
                // Fetch all races and find the one with the matching ID
                const response = await fetch(`${API_BASE_URL}/api/races`);
                if (!response.ok) {
                    throw new Error('Failed to fetch races');
                }
                const data = await response.json();
                const foundRace = data.find(r => String(r.race_id) === String(id));

                if (foundRace) {
                    setRace(foundRace);
                } else {
                    setError('Race not found');
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchRace();
        }
    }, [id]);

    // Helper functions (copied from RaceList to maintain atomic consistency without refactoring)
    const getGradeColor = (grade) => {
        const g = String(grade).toUpperCase();
        if (g.includes("G1")) return "bg-blue-600 border-blue-400 text-white shadow-blue-500/50";
        if (g.includes("G2")) return "bg-pink-600 border-pink-400 text-white shadow-pink-500/50";
        if (g.includes("G3")) return "bg-green-600 border-green-400 text-white shadow-green-500/50";
        if (g.includes("OP")) return "bg-orange-500 border-orange-300 text-white shadow-orange-500/50";
        return "bg-gray-600 border-gray-400 text-white shadow-gray-500/50";
    };

    const getDirectionIcon = (direction) => {
        const d = String(direction).toLowerCase();
        if (d.includes("clock") && !d.includes("count")) return <RotateCw className="w-8 h-8 text-indigo-400 opacity-80" />;
        if (d.includes("count") || d.includes("left")) return <RotateCcw className="w-8 h-8 text-indigo-400 opacity-80" />;
        return <MoveRight className="w-8 h-8 text-indigo-400 opacity-80" />;
    };

    if (loading) return <LoadingScreen />;

    if (error || !race) return (
        <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center p-4">
            <div className="text-red-400 mb-4 text-xl font-bold">{error || 'Race not found'}</div>
            <button
                onClick={() => navigate(ROUTES.RACES)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
            >
                {t('Common.back', 'Back to Races')}
            </button>
        </div>
    );

    const gradeColor = getGradeColor(race.grade);
    const placeholderUrl = createPlaceholderUrl(128, 64, t('RaceList.placeholder_banner'), '#1f2937', '#374151');
    const bannerSrc = race.banner_id
        ? `${API_BASE_URL}/api/thumb?img=/images/banner/races/banner_race_${race.banner_id}.png&width=128`
        : placeholderUrl;
    const trackBgSrc = race.course.track_id
        ? `${API_BASE_URL}/api/thumb?img=/images/track/${race.course.track_id}.png&width=512`
        : null;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
            <SEO
                title={race.race_name}
                description={`${race.race_name} - ${race.course.track_name} ${race.course.surface} ${race.course.distance}m`}
            />

            {/* --- HERO SECTION ---
                Intent: Track (512x512) centered as background, race banner (128x64) overlaid on top.
            */}
            <div className="relative w-full h-[50vh] min-h-[400px] overflow-hidden bg-gray-900 flex items-center justify-center">

                {/* Dark base layer */}
                <div className="absolute inset-0 bg-black/30" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />

                {/* Content Container: Track + Banner stacked */}
                <div className="relative z-10 flex items-center justify-center w-full h-full">

                    {/* Centered Composition: Track (back) + Race Banner (front) */}
                    <div className="relative flex items-center justify-center">

                        {/* Layer 1 (back): Track image - 512x512 centered, moderate opacity */}
                        {trackBgSrc && (
                            <FadeImage
                                src={trackBgSrc}
                                alt={race.course.track_name}
                                className="object-contain rounded-xl"
                                style={{ width: '512px', height: 'auto', opacity: 0.75 }}
                                fallback={null}
                            />
                        )}

                        {/* Layer 2 (front): Race banner centered on top of track */}
                        <div className="absolute flex flex-col items-center justify-center gap-2">
                            <div className="relative group">
                                {/* Glow effect */}
                                <div className={`absolute -inset-4 rounded-xl opacity-40 blur-xl ${gradeColor.split(' ')[0]}`}></div>

                                {/* Race banner: exact 128x64 */}
                                <div className="relative w-[128px] h-[64px] rounded shadow-2xl ring-1 ring-white/20 overflow-hidden">
                                    <FadeImage
                                        src={bannerSrc}
                                        alt={race.race_name}
                                        className="object-contain w-full h-full"
                                        style={{ width: '128px', height: '64px' }}
                                        fallback={placeholderUrl}
                                    />
                                </div>

                                {/* Grade Badge */}
                                <div className={`absolute -top-3 -right-6 px-2 py-0.5 rounded text-xs font-bold shadow-lg border backdrop-blur-md ${gradeColor} z-20`}>
                                    {race.grade}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* Back Button (Floating) */}
                <button
                    onClick={() => navigate(ROUTES.RACES)}
                    className="absolute top-24 left-4 md:left-8 z-20 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full backdrop-blur-sm transition-all border border-white/10 group"
                    title={t('Common.back', 'Back')}
                >
                    <MoveRight className="w-5 h-5 rotate-180 group-hover:-translate-x-1 transition-transform" />
                </button>
            </div>



            {/* --- DATA GRID SECTION --- 
                Intent: Display technical data clearly without wasting space.
                Technique: Responsive Grid (Bento Box style).
            */}
            <div className="container mx-auto px-4 max-w-7xl -mt-20 relative z-20">
                {/* Header Info (Name & Track) */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-6 md:p-10 mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2 text-sm font-bold tracking-wider text-blue-600 dark:text-blue-400 uppercase">
                            <MapPin className="w-4 h-4" />
                            {race.course.track_name}
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black text-gray-900 dark:text-white tracking-tight leading-tight">
                            {race.race_name}
                        </h1>
                    </div>
                    {/* Quick Stats Pill */}
                    <div className="flex items-center gap-3 bg-gray-100 dark:bg-gray-900/50 px-5 py-3 rounded-xl border border-gray-200 dark:border-gray-700">
                        <span className={String(race.course.surface).toLowerCase().includes('turf') ? 'text-green-600 dark:text-green-400 font-black text-xl uppercase' : 'text-yellow-700 dark:text-yellow-500 font-black text-xl uppercase'}>
                            {race.course.surface}
                        </span>
                        <div className="h-8 w-px bg-gray-300 dark:bg-gray-600 mx-2"></div>
                        <span className="text-gray-900 dark:text-white font-black text-xl font-mono">
                            {race.course.distance}m
                        </span>
                    </div>
                </div>

                {/* --- COURSE MAP SECTION --- */}
                {race.course.course_id && (
                    <div className="mb-8">
                        <CourseMap courseId={race.course.course_id} raceName={race.race_name} />
                    </div>
                )}

                {/* BENTOGRID for Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {/* Stat Card 1: Type */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 flex items-center justify-between group hover:border-blue-500/30 transition-colors">
                        <div>
                            <p className="text-sm font-bold text-gray-400 uppercase mb-1">{t('RaceList.label_type', 'Distance Type')}</p>
                            <p className="text-2xl font-bold text-gray-800 dark:text-white">{race.course.distance_type}</p>
                        </div>
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-blue-500">
                            <Ruler className="w-8 h-8 opacity-80" />
                        </div>
                    </div>

                    {/* Stat Card 2: Direction */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 flex items-center justify-between group hover:border-indigo-500/30 transition-colors">
                        <div>
                            <p className="text-sm font-bold text-gray-400 uppercase mb-1">Direction</p>
                            <p className="text-2xl font-bold text-gray-800 dark:text-white">{race.course.direction}</p>
                        </div>
                        <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl">
                            {getDirectionIcon(race.course.direction)}
                        </div>
                    </div>

                    {/* Stat Card 3: Trophy/Win (Visual Filler for now) */}
                    <div className="bg-gradient-to-br from-amber-100 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/10 p-6 rounded-2xl shadow-sm border border-amber-200 dark:border-amber-800/30 flex items-center gap-4">
                        <div className="p-3 bg-amber-500 text-white rounded-xl shadow-lg shadow-amber-500/40">
                            <Trophy className="w-8 h-8" />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-amber-800 dark:text-amber-400 uppercase mb-0.5">Race Class</p>
                            <p className="text-lg font-bold text-amber-900 dark:text-amber-100 leading-tight">Official Race</p>
                        </div>
                    </div>
                </div>

                {/* Schedules Timeline */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                        <Calendar className="w-6 h-6 text-pink-500" />
                        {t('RaceList.header_schedules', 'Event Schedule')}
                    </h3>

                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
                        {race.schedules && race.schedules.map((sched, idx) => (
                            <div key={idx} className="flex flex-col items-center p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                                <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">{sched.year}</span>
                                <span className="text-lg font-black text-blue-600 dark:text-blue-400">{sched.month}</span>
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-300 mt-1 px-2 py-0.5 bg-white dark:bg-gray-600 rounded-md border border-gray-200 dark:border-gray-500 shadow-sm">
                                    {sched.cycle}
                                </span>
                            </div>
                        ))}
                        {(!race.schedules || race.schedules.length === 0) && (
                            <div className="col-span-full text-center py-8 text-gray-400 flex flex-col items-center gap-2">
                                <Timer className="w-8 h-8 opacity-50" />
                                <span>No schedule data available</span>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default RaceDetail;
